import React from "react";
import { CheckCircle2, AlertCircle, XCircle, Check, X } from "lucide-react";
import { cn } from "../../../lib/utils";

const CHECKS: [string, string][] = [
  ["prefixPreserved", "Prior lineage preserved"],
  ["appendOnly", "Append-only (grew by ≤1)"],
  ["subConstant", "Same human at the root"],
  ["monotonicRoles", "Roles well-formed"],
  ["allVerified", "All identities verified"],
  ["scopePresent", "Scoped to one capability"],
];

const TONES = {
  danger: { border: "border-red-500/50", text: "text-red-600 dark:text-red-400" },
  warning: { border: "border-amber-500/50", text: "text-amber-600 dark:text-amber-400" },
  success: { border: "border-emerald-500/50", text: "text-emerald-600 dark:text-emerald-400" },
};

export default function GatewayOboIntegrity({ receipt }: { receipt: any }) {
  const inv = receipt && (receipt.obo_invariants || receipt.oboInvariants);
  if (!inv || typeof inv !== "object") return null;

  const structural = inv.structurallyValid !== false;
  const allHold = structural && inv.allVerified !== false && inv.scopePresent !== false;

  let tone: keyof typeof TONES;
  let Icon: typeof CheckCircle2;
  let title: string;
  if (!structural) {
    tone = "danger";
    Icon = XCircle;
    title = "Delegation integrity violated";
  } else if (!allHold) {
    tone = "warning";
    Icon = AlertCircle;
    title = "Structurally valid — with notes";
  } else {
    tone = "success";
    Icon = CheckCircle2;
    title = "Delegation integrity verified";
  }
  const t = TONES[tone];
  const violations = Array.isArray(inv.violations) ? inv.violations : [];

  return (
    <div className="rounded-lg border border-border px-3 py-2.5">
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <Icon className={cn("w-[15px] h-[15px] shrink-0", t.text)} />
        <strong className={cn("text-[13px] font-semibold", t.text)}>{title}</strong>
        <span className="text-[11px] text-muted-foreground/60">·</span>
        <span className="text-[11px] text-muted-foreground/80">checked at mint · faithful across the trace</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4">
        {CHECKS.map(([k, label]) => {
          const ok = inv[k] !== false;
          return (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              {ok ? (
                <Check className="w-[11px] h-[11px] text-emerald-600 dark:text-emerald-400 shrink-0" />
              ) : (
                <X className="w-[11px] h-[11px] text-red-600 dark:text-red-400 shrink-0" />
              )}
              {label}
            </span>
          );
        })}
      </div>
      {violations.length > 0 && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 break-words">{violations.join(" · ")}</div>
      )}
    </div>
  );
}
